import { useEffect, useState } from "react";
import { Building2, Plus, Pencil, Trash2, ImageUp } from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errorText } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { name: "", code: "", location: "", rooms: "", brand: "", is_active: true };

const PropertyDialog = ({ open, onOpenChange, editing, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(editing ? { ...EMPTY, ...editing, rooms: editing.rooms ?? "" } : EMPTY); }, [editing, open]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...form, rooms: form.rooms === "" ? null : Number(form.rooms) };
    try {
      if (editing) await api.patch(`/admin/properties/${editing.id}`, body);
      else await api.post("/admin/properties", body);
      toast.success(editing ? "Property updated" : "Property registered");
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="property-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{editing ? "Edit property" : "Register property"}</DialogTitle>
          <DialogDescription>Registered properties appear in the property selector when uploading actuals and creating budgets.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Property name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="e.g. SAMA Villa Amed" data-testid="property-name-input" /></div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="SVA" data-testid="property-code-input" /></div>
            <div className="space-y-1.5"><Label>Rooms / keys</Label><Input type="number" min="0" value={form.rooms} onChange={(e) => setForm({ ...form, rooms: e.target.value })} data-testid="property-rooms-input" className="font-mono" /></div>
            <div className="space-y-1.5"><Label>Brand</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Armella" data-testid="property-brand-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Amed, Bali" data-testid="property-location-input" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="property-active-checkbox" className="accent-emerald-600" /> Active (selectable for new budgets)</label>
          <Button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-emerald-700" data-testid="property-save-button">{busy ? "Saving..." : editing ? "Save changes" : "Register property"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function PropertiesPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/properties").then((r) => setItems(r.data)).catch((e) => toast.error(errorText(e)));
  useEffect(() => { load(); }, []);
  const uploadLogo = async (p, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/properties/${p.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Logo saved for ${p.name} — it appears on the Budget Pack cover next to the Armella logo`);
      load();
    } catch (e) { toast.error(errorText(e)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete property "${p.name}"?`)) return;
    try { await api.delete(`/admin/properties/${p.id}`); toast.success("Property deleted"); load(); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <AppNav />
      <main className="px-4 sm:px-8 lg:px-12 py-10 space-y-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 rise">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-emerald-700 font-semibold">Super admin</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mt-2">Properties</h1>
            <p className="text-slate-600 mt-3 max-w-2xl text-sm sm:text-base">Register every Armella Group property here. Users then pick the property before uploading actual P&amp;L files or creating a budget.</p>
          </div>
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="h-11 bg-slate-900 hover:bg-emerald-700 px-5" data-testid="create-property-button"><Plus size={16} className="mr-2" /> Register property</Button>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg overflow-auto" data-testid="properties-table">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900 text-white text-[11px] uppercase tracking-wider">
              <tr><th className="text-left px-4 py-3">Property</th><th className="text-left px-4 py-3">Code</th><th className="text-left px-4 py-3">Location</th><th className="text-right px-4 py-3">Rooms</th><th className="text-right px-4 py-3">Budgets</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3" /></tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`property-row-${p.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <td className="px-4 py-3"><p className="font-medium text-slate-900 inline-flex items-center gap-2"><Building2 size={14} className="text-emerald-600" /> {p.name}</p>{p.brand && <p className="text-xs text-slate-500 ml-6">{p.brand}</p>}</td>
                  <td className="px-4 py-3 font-mono text-xs">{p.code || "-"}</td>
                  <td className="px-4 py-3 text-slate-700">{p.location || "-"}</td>
                  <td className="px-4 py-3 text-right font-mono">{p.rooms ?? "-"}</td>
                  <td className="px-4 py-3 text-right font-mono">{p.projects}</td>
                  <td className="px-4 py-3">{p.is_active ? <Badge className="bg-emerald-100 text-emerald-800 border-0">Active</Badge> : <Badge className="bg-slate-200 text-slate-600 border-0">Inactive</Badge>}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <label className="inline-flex items-center gap-1 text-slate-400 hover:text-emerald-700 p-1 cursor-pointer align-middle" title={p.has_logo ? "Replace property logo (shown on the Budget Pack cover)" : "Upload property logo (shown on the Budget Pack cover)"} data-testid={`logo-upload-${p.id}`}>
                      {p.has_logo ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/properties/${p.id}/logo?t=${p.updated_at || ""}`} alt="logo" className="h-5 max-w-[60px] object-contain" data-testid={`logo-preview-${p.id}`} /> : <ImageUp size={15} />}
                      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadLogo(p, e.target.files?.[0])} data-testid={`logo-input-${p.id}`} />
                    </label>
                    <button onClick={() => { setEditing(p); setOpen(true); }} className="text-slate-400 hover:text-emerald-700 p-1" data-testid={`edit-property-${p.id}`}><Pencil size={15} /></button>
                    <button onClick={() => remove(p)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`delete-property-${p.id}`}><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No properties registered yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </main>
      <PropertyDialog open={open} onOpenChange={setOpen} editing={editing} onSaved={load} />
    </div>
  );
}
